import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditVeterinaryInformationContent } from './content'
import { EditVeterinaryInformationControls } from './controls'

export enum Input {
  VetName = 'vet_name',
  VetPhone = 'vet_phone',
}

export const editVeterinaryInformationSchema = z.object({
  [Input.VetName]: z.string()
    .max(255, { message: 'Vet name must be less than 255 characters.' })
    .optional(),
  [Input.VetPhone]: z.string()
    .max(32, { message: 'Vet phone number must be less than 32 characters.' })
    .optional(),
})

export type EditVeterinaryInformationSchema = z.infer<typeof editVeterinaryInformationSchema>

type EditVeterinaryInformationFormContextType = {
  onSubmit: (values: EditVeterinaryInformationSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditVeterinaryInformationFormContext = createContext<EditVeterinaryInformationFormContextType | undefined>(undefined)

type EditVeterinaryInformationFormProps = EditVeterinaryInformationFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditVeterinaryInformationSchema>,
}

export function EditVeterinaryInformationForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditVeterinaryInformationFormProps) {

  const context: EditVeterinaryInformationFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditVeterinaryInformationFormContext.Provider value={context}>
      <Form<EditVeterinaryInformationSchema, typeof editVeterinaryInformationSchema>
        schema={editVeterinaryInformationSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditVeterinaryInformationFormContext.Provider>
  )
}

export function useEditVeterinaryInformationForm() {
  const context = useContext(EditVeterinaryInformationFormContext)
  if (context === undefined) {
    throw new Error('useEditVeterinaryInformationForm must be used within a EditVeterinaryInformationFormProvider')
  }
  return context
}

// Compound components
EditVeterinaryInformationForm.Content = EditVeterinaryInformationContent
EditVeterinaryInformationForm.Controls = EditVeterinaryInformationControls
